import { apiFetch, isErrorResult } from "../../../../app/apiClient.js";
import { hasPermission } from "../../../../app/auth.js";
import { updateFilterOptions } from "../utils/filterManager.js";
import { currentTable, getDateFilter } from "../../../../app/viewManager.js";
import { formatDateDE } from "../utils/timeUtils.js";
import { formatDateISO } from "../../../shared/app-input/utils/dateUtils.js";
import { getContent, validateData, markError, removeStaleRow, deleteRecord } from "./tableManager.js";
import { toast } from "../../../layout/app-toast/utils/toastManager.js";
import { currentContext } from "../../app-dialog/utils/core/dialogContext.js";

function pad(num) {
    return String(num).padStart(2, "0");
}

function formatTime(isoOrString) {
    if (!isoOrString) return "";
    const date = new Date(isoOrString);
    if (Number.isNaN(date.getTime())) return "";
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDate(isoOrString) {
    if (!isoOrString) return "";
    const datePart = String(isoOrString).split("T")[0];
    return formatDateDE(datePart);
}

export function formatTimeSpan(timeSpan) {
    if (!timeSpan) return "00:00";

    let totalMinutes = 0;
    const str = String(timeSpan);
    const dayMatch = str.match(/^(\d+)\.(.*)$/);
    let rest = str;
    if (dayMatch) {
        totalMinutes += parseInt(dayMatch[1], 10) * 24 * 60;
        rest = dayMatch[2];
    }
    const parts = rest.split(":");
    if (parts.length >= 2) {
        totalMinutes += parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    }
    if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return "00:00";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${pad(hours)}:${pad(minutes)}`;
}

function formatBreakSegments(segments) {
    if (!segments) return "";
    if (Array.isArray(segments)) {
        return segments.map(s => {
            if (typeof s === "object" && s !== null) {
                const start = formatTime(s.start ?? s.startTime);
                const end = formatTime(s.end ?? s.endTime);
                return start && end ? `${start}-${end}` : (start || end);
            }
            return String(s);
        }).filter(Boolean).join("; ") + ";";
    }
    return String(segments).split(/\s+/).filter(Boolean).join("; ") + ";";
}

function formatTimeLogs(data) {
    return data.map(t => ({
        id: t.id,
        memberId: t.userId,
        memberName: t.userName ?? "",
        teamId: t.teamId ?? "",
        teamName: t.teamName ?? "",
        date: t.date,

        start: formatTime(t.startTime),
        end: formatTime(t.endTime),

        breakSegments: formatBreakSegments(t.breakSegments),
        breakSegmentsRaw: t.breakSegments ?? [],

        breakTime: formatTimeSpan(t.breakTime),
        duration: formatTimeSpan(t.duration),

        isDayOff: t.isDayOff ?? false,
        isWeekend: t.isWeekend ?? false,
        isAutoGeneratedAbsence: t.isAutoGeneratedAbsence ?? false,
    }));
}

export async function addFinishedTimeLog(table) {
    const today = new Date().toISOString().split('T')[0];
    const data = await apiFetch(`WorkDay?from=${today}&to=${today}&scope=User`);
    if (!Array.isArray(data) || data.length === 0) return;
    const rows = formatTimeLogs(data);
    for (const row of rows) {
        if (table.getRow(row.id)) {
            await table.updateData([row]);
        } else {
            await table.addRow(row, true);
        }
    }
    updateFilterOptions(table.getData(), table);
}

export async function loadTimeLogs(table) {
    const timeLogTable = table ?? currentTable;
    if (!timeLogTable) return false;

    const params = new URLSearchParams();
    const dateFilter = getDateFilter('Timelogs');

    if (dateFilter?.mode === 'exact') {
        if (dateFilter.from) params.append('from', dateFilter.from);
        if (dateFilter.to)   params.append('to',   dateFilter.to);
    } else if (dateFilter?.mode === 'coarse') {
        const year   = dateFilter.year;
        const months = Array.isArray(dateFilter.months) ? dateFilter.months : [];
        if (year && months.length >= 1) {
            const monthNum = parseInt(months[0], 10);
            params.append('from', formatDateISO(new Date(Number(year), monthNum - 1, 1)));
            params.append('to',   formatDateISO(new Date(Number(year), monthNum,     0)));
        } else if (year) {
            params.append('from', `${year}-01-01`);
            params.append('to',   `${year}-12-31`);
        }
    }

    const filterValue = sessionStorage.getItem('filterTimelogs') || 'user';

    if (filterValue === 'team' && (hasPermission("Timelogs.Read.Team") || hasPermission("Timelogs.Read.Any"))) {
        params.append('scope', 'Team');
    } else if (filterValue === 'all' && hasPermission("Timelogs.Read.Any")) {
        params.append('scope', 'All');
    } else {
        params.append('scope', 'User');
    }

    const query = params.toString();
    const data = await apiFetch(query ? `WorkDay?${query}` : "WorkDay");

    timeLogTable.options.placeholder = "Keine Daten vorhanden";

    if (!Array.isArray(data)) {
        timeLogTable.replaceData([]);
        return false;
    }

    const rows = formatTimeLogs(data);
    timeLogTable.setData(rows);
    updateFilterOptions(rows, timeLogTable);
    return true;
}

function toMinutes(hhmm) {
    const [h, m] = String(hhmm).split(":").map(Number);
    return h * 60 + m;
}

function collectBreakSegments(content) {
    const rows = [...(content?.querySelectorAll("#breakSegmentsList .breakSegmentRow") ?? [])];
    return rows
        .map(row => ({
            Start: row.querySelector('[data-break-field="start"]')?.getValue?.() || null,
            End:   row.querySelector('[data-break-field="end"]')?.getValue?.() || null,
        }))
        .filter(segment => segment.Start || segment.End);
}

function combineDateTime(datePart, timeStr) {
    return (datePart && timeStr) ? `${datePart}T${timeStr}:00` : null;
}

async function collectTimeLogData(datePart = null) {
    try {
        const content = getContent();
        if (!content) throw new Error("Dialog content nicht gefunden");

        const startEl = content.querySelector("#inputTimeLogStart");
        const endEl   = content.querySelector("#inputTimeLogEnd");
        if (!startEl) throw new Error("Element #inputTimeLogStart nicht gefunden");

        const dateElement = content.querySelector("#inputTimeLogDate");
        const date = dateElement ? (dateElement.getValue?.() || null) : datePart;

        if (!date) {
            toast("Datum konnte nicht ermittelt werden. Bitte den Dialog neu öffnen.", "error");
            return null;
        }

        const startVal = startEl.getValue?.() || null;
        const endVal   = endEl?.getValue?.()   || null;

        const data = {
            StartTime: combineDateTime(date, startVal),
            EndTime:   combineDateTime(date, endVal),
            BreakSegments: collectBreakSegments(content).map(segment => ({
                Start: combineDateTime(date, segment.Start),
                End:   combineDateTime(date, segment.End),
            })),
        };
        if (dateElement) data.Date = date;

        return data;
    } catch (error) {
        console.error("Fehler beim Ermitteln der Daten:", error.message);
        return null;
    }
}

function validateTimeLogTimes(content, data) {
    if (data.EndTime && new Date(data.EndTime) <= new Date(data.StartTime)) {
        markError(content?.querySelector("#inputTimeLogEnd"));
        toast("Die Endzeit muss nach der Startzeit liegen", "error");
        return false;
    }

    const breakRows = [...(content?.querySelectorAll("#breakSegmentsList .breakSegmentRow") ?? [])];
    for (const row of breakRows) {
        const startEl = row.querySelector('[data-break-field="start"]');
        const endEl   = row.querySelector('[data-break-field="end"]');
        const start   = startEl?.getValue?.();
        const end     = endEl?.getValue?.();

        if (!start || !end) {
            markError(start ? endEl : startEl);
            toast("Bitte fülle Start und Ende jeder Pause aus", "error");
            return false;
        }
        if (toMinutes(end) <= toMinutes(start)) {
            markError(endEl);
            toast("Das Ende einer Pause muss nach ihrem Start liegen", "error");
            return false;
        }
    }

    return true;
}

export async function createTimeLog() {
    const collectedData = await collectTimeLogData();
    if (!collectedData) return;

    const missingFields = await validateData(collectedData);
    if (missingFields.length > 0) return true;

    if (!validateTimeLogTimes(getContent(), collectedData)) return true;

    const raw = await apiFetch("WorkDay", "POST", collectedData);
    if (isErrorResult(raw)) return raw ? true : undefined;

    const newRow = formatTimeLogs([raw])[0];
    if (currentTable?.tableName === "timeLog") {
        currentTable.addRow(newRow, true);
        updateFilterOptions(currentTable.getData(), currentTable);
    }

    toast("Zeiterfassung erfolgreich erstellt!", "success");
    return raw;
}

export async function updateTimeLog(timeLogId) {
    const rowDate = currentTable?.getRow(timeLogId)?.getData()?.date;
    const collectedData = await collectTimeLogData(rowDate ?? currentContext.date);
    if (!collectedData) return;

    const missingFields = await validateData(collectedData);
    if (missingFields.length > 0) return true;

    if (!validateTimeLogTimes(getContent(), collectedData)) return true;

    const raw = await apiFetch(`WorkDay/${timeLogId}`, "PATCH", collectedData);
    if (removeStaleRow(raw, timeLogId)) return;
    if (isErrorResult(raw)) return raw ? true : undefined;

    const updatedRow = formatTimeLogs([raw])[0];
    currentTable.updateData([updatedRow]);
    updateFilterOptions(currentTable.getData(), currentTable);

    toast("Zeiterfassung erfolgreich aktualisiert!", "success");
    return raw;
}

export const deleteTimeLog = (timeLogId) => deleteRecord("WorkDay", timeLogId, "Zeiterfassung erfolgreich gelöscht!");
